import { Button, Flex, Spinner, Stack, Td, Text } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  ClimateWeightingGroup,
  useGetProcurementClimateWeightingListQuery,
  type ClimateWeightingDto,
  type ClimateWeightingRowDto,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../common/errors/useApiError";
import { ListLoadError } from "../../common/ListLoadError";
import { formatNumber } from "../../common/formatting/formatNumber";
import { WIDE_ONLY_CELL } from "../../common/responsiveTable";
import { urls } from "../../urls";
import { exemptionTitleKey } from "../sourcing-events/buyer/wizard/utils/environmentJustificationTemplates";
import { PanelRow, PanelTable, TitleCell } from "../home/panels/PanelTable";
import { OverviewBar } from "./OverviewBar";
import type { PeriodRange } from "./procurementFigures";

export const parseClimateWeightingGroup = (value: string | null): ClimateWeightingGroup | null =>
  Object.values(ClimateWeightingGroup).find((group) => group === value) ?? null;

export const climateWeightingGroupLabel = (t: TFunction, group: ClimateWeightingGroup): string =>
  ({
    [ClimateWeightingGroup.Weighted]: t("Weighted at least 30% climate and environment"),
    [ClimateWeightingGroup.SpecificationInstead]: t("Climate requirements in the specification instead"),
    [ClimateWeightingGroup.Exemption]: t("Exemption with justification"),
    [ClimateWeightingGroup.MissingJustification]: t("Missing justification"),
  })[group];

const percentOf = (part: number, whole: number): string =>
  `${formatNumber(whole === 0 ? 0 : Math.round((part / whole) * 1000) / 10)} %`;

export const ClimateWeighting = ({
  climate,
  groupLink,
}: {
  climate: ClimateWeightingDto;
  groupLink: (group: ClimateWeightingGroup) => string;
}) => {
  const { t } = useTranslation();
  const total = climate.groups.reduce((sum, { procurements }) => sum + procurements, 0);
  return (
    <Stack
      spacing="3"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="lg"
      p="4"
      bg="smBackground"
      data-testid="climate-weighting"
    >
      <Text as="h2" fontWeight="semibold" color="smText">
        {t("Climate and environmental weighting (anskaffelsesloven § 5b)")}
      </Text>
      {total === 0 ? (
        <Text fontSize="sm" color="smMuted" data-testid="no-climate-weighting">
          {t("No procurement with weighted award criteria under FOA Part II or III was announced in the period.")}
        </Text>
      ) : (
        <Stack spacing="1">
          {climate.groups.map(({ group, procurements }) => (
            <OverviewBar
              key={group}
              label={climateWeightingGroupLabel(t, group)}
              width={(procurements / total) * 100}
              shown={`${formatNumber(procurements)} · ${percentOf(procurements, total)}`}
              details={[
                String(t("{{count}} procurements announced in the period", { count: procurements })),
                String(t("{{share}} of the procurements with weighted award criteria", { share: percentOf(procurements, total) })),
              ]}
              link={procurements > 0 ? groupLink(group) : null}
              testId={`climate-${group}`}
            />
          ))}
        </Stack>
      )}
      <Stack spacing="1" fontSize="sm" color="smMuted">
        <Text>
          {t(
            "Procurements under FOA Part II and III with a contract notice, announced in the period, whose award criteria are weighted. Where the criteria are ranked instead, climate and environment among the three highest counts as weighted. Procurements under the utilities regulation are not included.",
          )}
        </Text>
        {climate.withoutWeightedCriteria > 0 && (
          <Text data-testid="climate-without-weighted-criteria">
            {t("{{count}} procurements announced in the period have no weighted award criteria, such as lowest price alone, and are not included.", {
              count: climate.withoutWeightedCriteria,
            })}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};

export const ClimateWeightingProcurements = ({
  group,
  range,
  closeLink,
}: {
  group: ClimateWeightingGroup;
  range: PeriodRange;
  closeLink: string;
}) => {
  const { t } = useTranslation();
  const organizationId = useLoggedInWithOrgContextState().selectedOrg.id;
  const { currentData: data, isFetching, error, refetch } = useGetProcurementClimateWeightingListQuery({
    orgId: organizationId,
    group,
    from: range.from,
    to: range.to,
  });
  const { message } = useApiError();
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.scrollIntoView?.({ block: "nearest" });
    heading.current?.focus();
  }, [group]);

  return (
    <Stack
      spacing="3"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="lg"
      p="4"
      bg="smBackground"
      data-testid="climate-weighting-procurements"
    >
      <Flex justify="space-between" align="baseline" gap="3" wrap="wrap">
        <Text as="h2" ref={heading} tabIndex={-1} fontWeight="semibold" color="smText" _focus={{ outline: "none" }}>
          {t("{{group}}: the procurements", { group: climateWeightingGroupLabel(t, group) })}
        </Text>
        <Button as={RouterLink} to={closeLink} size="sm" variant="link" colorScheme="teal">
          {t("Close")}
        </Button>
      </Flex>
      {error ? (
        <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />
      ) : !data ? (
        <Spinner color="smPrimary" data-testid="climate-weighting-procurements-loading" />
      ) : data.newestFirst.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No procurement announced in the period is in this group.")}
        </Text>
      ) : (
        <>
          <Text fontSize="sm" color="smMuted">
            {data.procurements > data.newestFirst.length
              ? t("The {{shown}} announced most recently, of {{count}} procurements.", {
                  shown: formatNumber(data.newestFirst.length),
                  count: data.procurements,
                })
              : t("{{count}} procurements, the most recently announced first.", { count: data.procurements })}
          </Text>
          <Flex overflowX="auto">
            <PanelTable headers={[t("Procurement"), detailHeader(t, group)].map(String)} phoneColumns={1}>
              {data.newestFirst.map((row) => (
                <PanelRow key={row.id} to={urls.app.sourcingEvents.view.go(row.id)}>
                  <TitleCell
                    title={row.title || String(t("Untitled"))}
                    subtitle={row.referenceId ?? undefined}
                    wrapTitle
                    width={{ base: "100%", md: "50%" }}
                    phoneDetail={<RowDetail row={row} group={group} />}
                  />
                  <Td whiteSpace="normal" display={WIDE_ONLY_CELL} w="50%" data-testid={`climate-row-${row.id}`}>
                    <RowDetail row={row} group={group} />
                  </Td>
                </PanelRow>
              ))}
            </PanelTable>
          </Flex>
        </>
      )}
    </Stack>
  );
};

const detailHeader = (t: TFunction, group: ClimateWeightingGroup): string =>
  group === ClimateWeightingGroup.Weighted ? t("Climate and environment") : t("Justification");

const RowDetail = ({ row, group }: { row: ClimateWeightingRowDto; group: ClimateWeightingGroup }) => {
  const { t } = useTranslation();
  const weighting =
    row.environmentRank != null
      ? t("Ranked {{rank}} of the award criteria", { rank: row.environmentRank })
      : row.environmentWeightPercent
        ? t("Weighted {{weight}} %", { weight: formatNumber(row.environmentWeightPercent) })
        : t("No climate and environment criterion");
  return (
    <Stack spacing="0.5" fontSize="sm">
      {row.exemption && (
        <Text fontWeight="semibold" color="smText">
          {t(exemptionTitleKey(row.exemption))}
        </Text>
      )}
      {row.justification ? (
        <Text color="smText">{row.justification}</Text>
      ) : (
        <>
          {!row.exemption && <Text color="smText">{weighting}</Text>}
          {group === ClimateWeightingGroup.MissingJustification && (
            <Text color="smMuted">
              {row.justificationIsUnfinishedStandardText
                ? t("The justification is the standard text, with its placeholder not filled in")
                : t("No justification written")}
            </Text>
          )}
        </>
      )}
    </Stack>
  );
};
